import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Alert,
  Box,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import React, { useState } from "react";

import {
  applyRestOfMonth,
  formatMonthLabelWithRestOf,
  getCurrentMonthKey,
  getRestOfMonthRemainingFraction,
} from "./restOfMonthUtils";

const StyledTableRow = styled(TableRow)(
  ({ theme, isEvenLogicalRow, isLastInGroup, expanded }) => ({
    backgroundColor: isEvenLogicalRow ? "#f8fafc" : "#ffffff",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: "#f1f5f9",
      transform: "translateX(2px)",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    },
    "& .MuiTableCell-root": {
      borderBottom:
        expanded || !isLastInGroup
          ? "none"
          : `1px solid ${theme.palette.divider}`,
      padding: "16px 12px",
      fontSize: "13px",
      color: "#1e293b",
    },
  })
);

export const getForecastData = (
  childAsin,
  predictedUnits,
  sellerForecastReviews
) => {
  const predictedItem = predictedUnits.find(({ asin }) => asin === childAsin);
  const sellerForecastItem = sellerForecastReviews?.find(
    (item) =>
      item.asin === childAsin &&
      item.forecast_date === predictedItem?.calculated_at &&
      item.review === "override"
  );

  return {
    original: predictedItem?.forecast_total || 0,
    override: sellerForecastItem?.override?.total,
    hasOverride: !!sellerForecastItem,
    predictedItem,
    sellerForecastItem,
  };
};

// Approximate lead time (in days) from placing a PO to units becoming
// available on a customer-facing channel. Demand for months whose start falls
// inside this window cannot realistically be served by a freshly placed PO,
// so for any such month that is also projected to go OOS we exclude its
// forecasted demand from the PO sizing (and surface that with a NOT-PO
// indicator next to the OOS chip in the monthly projection).
const PO_LEAD_TIME_DAYS = 60;

const isMonthWithinDays = (monthKey, days) => {
  const [y, m] = String(monthKey).split("-").map(Number);
  if (!y || !m) return false;
  const monthStart = new Date(y, m - 1, 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cutoff = new Date(today);
  cutoff.setDate(cutoff.getDate() + days);
  return monthStart <= cutoff;
};

// PO sizing based on the monthly projection. We sum the *actual* per-month
// shortfalls from the roll-forward (each month's negative end-of-month balance)
// rather than netting total demand against total inventory. This keeps the PO
// on the same inventory basis as the projection: on-hand is consumed month by
// month (and floored at 0 between months), so inventory eaten by an early month
// can't leak forward to offset a later one.
//
// Demand for OOS months that fall inside the PO lead-time window is still
// excluded, because units we order today won't arrive in time to serve them;
// we simply don't add their shortfall to the PO. Because the roll-forward
// already floors carry-over at 0, excluding those months does not silently
// free up inventory for later months.
//
// Note: `start`/`incoming` in the roll-forward only reflect inventory that is
// customer-facing now or incoming with an ETA. Units in the *_inventory totals
// that lack an ETA (see the "unscheduled incoming" alert) are intentionally not
// credited here, so the PO may be larger than the old all_inventory-based math.
export const calculatePOFromProjection = (projection) =>
  (projection || []).reduce(
    (sum, m) => sum + (m.excludedFromPO ? 0 : Math.max(-m.end, 0)),
    0
  );

// Inventory buckets that are immediately customer-facing or already in motion
// to a customer-facing channel. We use these as the starting on-hand for the
// monthly roll-forward, deliberately excluding `on_the_way_inventory`,
// `in_work_inventory`, and `awd_inbound` until those carry per-shipment ETAs
// from the backend (see `incoming_shipments` proposed shape below).
const getAvailableNow = (row) =>
  (row.fba_inventory || 0) +
  (row.awd_inventory || 0) +
  (row["3pl_inventory"] || 0) +
  (row.awd_outbound || 0);

// Scalar inventory buckets that represent units in motion but not yet on the
// timeline, paired with the row-level ETA field the backend provides for them.
// When a bucket total isn't fully covered by explicit `incoming_shipments`
// entries and we have a scalar ETA, we synthesize a shipment for the remainder
// so those units land on the projection instead of being dropped.
const BUCKET_TOTAL_FIELD = {
  on_the_way: "on_the_way_inventory",
  in_work: "in_work_inventory",
  awd_inbound: "awd_inbound",
};
const BUCKET_ETA_FIELD = {
  on_the_way: "on_the_way_eta",
  in_work: "in_work_eta",
  awd_inbound: "awd_inbound_eta",
};

// Real `incoming_shipments` plus synthesized entries for any scalar bucket that
// carries a row-level ETA but isn't (fully) represented as explicit shipments.
// This keeps every downstream consumer (timeline bucketing, the unscheduled
// alert, CSV export) working off one consistent view of incoming units.
export const getEffectiveShipments = (row) => {
  const shipments = Array.isArray(row.incoming_shipments)
    ? [...row.incoming_shipments]
    : [];
  Object.entries(BUCKET_TOTAL_FIELD).forEach(([type, totalField]) => {
    const total = Number(row[totalField]) || 0;
    if (total <= 0) return;
    const scheduled = shipments
      .filter((s) => s && s.type === type && s.eta && s.quantity)
      .reduce((acc, s) => acc + (Number(s.quantity) || 0), 0);
    const remainder = Math.max(total - scheduled, 0);
    const eta = row[BUCKET_ETA_FIELD[type]];
    if (remainder > 0 && eta) {
      shipments.push({ type, quantity: remainder, eta, synthetic: true });
    }
  });
  return shipments;
};

// Bucket incoming shipments by `YYYY-MM` of their ETA.
//
// Expected shape on each active-inventory row (backend, not yet shipped):
//   incoming_shipments: [
//     {
//       type: "on_the_way" | "awd_inbound", // (extendable)
//       quantity: 540,
//       eta: "2026-05-15",          // ISO date
//       shipment_id: "SHP-12345",   // optional, for UI tooltips
//       source: "factory" | "awd"   // optional
//     },
//     ...
//   ]
const bucketIncomingByMonth = (row) => {
  const buckets = {};
  const shipments = getEffectiveShipments(row);
  shipments.forEach((s) => {
    if (!s || !s.eta || !s.quantity) return;
    const monthKey = String(s.eta).slice(0, 7); // "YYYY-MM"
    if (!buckets[monthKey]) buckets[monthKey] = { qty: 0, shipments: [] };
    buckets[monthKey].qty += Number(s.quantity) || 0;
    buckets[monthKey].shipments.push(s);
  });
  return buckets;
};

export const buildMonthlyProjection = (
  row,
  predictedItem,
  sellerForecastItem
) => {
  if (!predictedItem || !predictedItem.predictions) return [];

  const monthKeys = Object.keys(predictedItem.predictions).sort();
  const incomingByMonth = bucketIncomingByMonth(row);

  // Per-month overrides (if a seller review override exists).
  // Falls back to scaling the original prediction by total override / total
  // original when no per-month breakdown is available.
  const overrideByMonths =
    sellerForecastItem?.override?.override_by_months ||
    sellerForecastItem?.override?.overrideByMonths ||
    null;
  const originalTotal = monthKeys.reduce(
    (sum, k) => sum + (predictedItem.predictions[k]?.value || 0),
    0
  );
  const overrideTotal = sellerForecastItem?.override?.total;
  const scale =
    overrideByMonths || !overrideTotal || !originalTotal
      ? null
      : overrideTotal / originalTotal;

  // Top-level fall-backs for legacy reviews that stored reason/notes as a
  // single string or as a month-keyed dict at the review level rather than
  // inside `override_by_months`.
  const topLevelReason = sellerForecastItem?.override_reason;
  const topLevelNotes = sellerForecastItem?.override_notes;
  const getReasonFor = (monthKey) => {
    const fromMonth = overrideByMonths?.[monthKey]?.reason;
    if (fromMonth) return fromMonth;
    if (topLevelReason && typeof topLevelReason === "object") {
      return topLevelReason[monthKey] || "";
    }
    if (typeof topLevelReason === "string") return topLevelReason;
    return "";
  };
  const getNotesFor = (monthKey) => {
    const fromMonth = overrideByMonths?.[monthKey]?.notes;
    if (fromMonth) return fromMonth;
    if (topLevelNotes && typeof topLevelNotes === "object") {
      return topLevelNotes[monthKey] || "";
    }
    if (typeof topLevelNotes === "string") return topLevelNotes;
    return "";
  };

  let onHand = getAvailableNow(row);
  return monthKeys.map((monthKey) => {
    const original = predictedItem.predictions[monthKey]?.value || 0;
    let forecastFullMonth = original;
    if (overrideByMonths && overrideByMonths[monthKey]) {
      forecastFullMonth = Number(overrideByMonths[monthKey].value) || 0;
    } else if (scale != null) {
      forecastFullMonth = Math.round(original * scale);
    }

    const remainingFraction = getRestOfMonthRemainingFraction(monthKey);
    const isRestOfMonth = monthKey === getCurrentMonthKey();
    const forecast = applyRestOfMonth(monthKey, forecastFullMonth);

    const isOverridden = forecastFullMonth !== original;
    const overrideReason = isOverridden ? getReasonFor(monthKey) : "";
    const overrideNotes = isOverridden ? getNotesFor(monthKey) : "";

    const incoming = incomingByMonth[monthKey]?.qty || 0;
    const incomingShipments = incomingByMonth[monthKey]?.shipments || [];

    // "start" is the carry-over from the previous month's EOM (or the current
    // available inventory for the first month). Incoming is shown separately
    // so the row reads as: start + incoming - forecast = end.
    const start = onHand;
    const end = start + incoming - forecast;

    let status = "ok";
    if (end < 0) status = "oos";
    else if (forecast > 0 && end < forecast * 0.25) status = "low";

    onHand = Math.max(end, 0);

    const withinLeadTime = isMonthWithinDays(monthKey, PO_LEAD_TIME_DAYS);
    const excludedFromPO = withinLeadTime && status === "oos";

    return {
      monthKey,
      label: formatMonthLabelWithRestOf(monthKey),
      start,
      incoming,
      incomingShipments,
      forecast,
      forecastFullMonth,
      forecastOriginal: original,
      isRestOfMonth,
      restOfMonthRemainingFraction: remainingFraction,
      overrideReason,
      overrideNotes,
      end,
      status,
      withinLeadTime,
      excludedFromPO,
    };
  });
};

const STATUS_STYLES = {
  ok: { label: "OK", bg: "#dcfce7", color: "#166534" },
  low: { label: "Low", bg: "#fef3c7", color: "#92400e" },
  oos: { label: "OOS", bg: "#fee2e2", color: "#991b1b" },
};

const StatusChip = ({ status, excludedFromPO }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.ok;
  return (
    <Box sx={{ position: "relative", width: "100%", minHeight: 20 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Chip
          label={s.label}
          size="small"
          sx={{
            backgroundColor: s.bg,
            color: s.color,
            fontWeight: 700,
            fontSize: "11px",
            height: "20px",
          }}
        />
      </Box>
      {excludedFromPO && (
        <Tooltip
          arrow
          title={`This OOS month falls inside the PO lead time (~${PO_LEAD_TIME_DAYS} days), so units ordered now won't arrive in time. Its forecasted demand is excluded from the PO total.`}
        >
          <Chip
            label="NIP"
            size="small"
            sx={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "transparent",
              color: "#000",
              fontWeight: 700,
              fontSize: "11px",
              height: "20px",
              border: "1px solid #000",
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

const MonthlyProjectionPanel = ({ row, predictedItem, sellerForecastItem }) => {
  const projection = buildMonthlyProjection(
    row,
    predictedItem,
    sellerForecastItem
  );

  if (!projection.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ color: "#64748b" }}>
          No monthly forecast available for this ASIN.
        </Typography>
      </Box>
    );
  }

  // Breakdown of incoming units that are not placed on the timeline. Those
  // units exist (the legacy *_inventory totals know about them) but they have
  // no ETA yet, so they are excluded from the projection. The *reason* an ETA
  // is missing varies per shipment (e.g. still in "inspection"), so we attribute
  // each unscheduled unit to its shipment stage/status where we can, and only
  // fall back to the generic inventory bucket when no shipment record exists.
  const shipments = getEffectiveShipments(row);
  const unscheduledLabel = (type) =>
    ({
      on_the_way: "on the way",
      in_work: "in work",
      awd_inbound: "AWD inbound",
    }[type] || type);

  const scheduledByType = shipments.reduce((acc, s) => {
    if (s && s.eta && s.quantity) {
      acc[s.type] = (acc[s.type] || 0) + (Number(s.quantity) || 0);
    }
    return acc;
  }, {});
  const totalsByType = {
    on_the_way: row.on_the_way_inventory || 0,
    in_work: row.in_work_inventory || 0,
    awd_inbound: row.awd_inbound || 0,
  };
  const unscheduledByType = Object.fromEntries(
    Object.entries(totalsByType).map(([type, total]) => [
      type,
      Math.max(total - (scheduledByType[type] || 0), 0),
    ])
  );
  const totalUnscheduled = Object.values(unscheduledByType).reduce(
    (a, b) => a + b,
    0
  );

  // Prefer a per-stage breakdown built from the actual unscheduled shipments
  // (those with quantity but no ETA), so the message shows the real reason
  // (e.g. "inspection") rather than blaming a missing ETA. Any unscheduled
  // units without a matching shipment record fall back to their inventory
  // bucket label.
  const addTo = (obj, key, qty) => {
    if (qty > 0) obj[key] = (obj[key] || 0) + qty;
  };
  const unscheduledByReason = {};
  Object.entries(unscheduledByType).forEach(([type, unscheduledQty]) => {
    if (unscheduledQty <= 0) return;
    let attributed = 0;
    shipments
      .filter((s) => s && s.type === type && s.quantity && !s.eta)
      .forEach((s) => {
        const qty = Number(s.quantity) || 0;
        addTo(unscheduledByReason, s.stage || unscheduledLabel(type), qty);
        attributed += qty;
      });
    // Legacy totals can exceed the shipment records we have; bucket the rest
    // under the generic inventory label.
    addTo(
      unscheduledByReason,
      unscheduledLabel(type),
      Math.max(unscheduledQty - attributed, 0)
    );
  });
  const breakdown = Object.entries(unscheduledByReason).filter(
    ([, qty]) => qty > 0
  );

  return (
    <Box sx={{ p: 2, backgroundColor: "#f1f5f9" }}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}
      >
        Month-by-month projection &middot; {row.sku}
      </Typography>

      {totalUnscheduled > 0 && (
        <Alert
          severity="info"
          sx={{
            mb: 1.5,
            fontSize: "12px",
            "& .MuiAlert-message": { padding: "4px 0" },
          }}
        >
          {totalUnscheduled.toLocaleString()} incoming units are counted in the
          inventory totals (
          {breakdown
            .map(([reason, qty]) => `${reason}: ${qty.toLocaleString()}`)
            .join(", ")}
          ) but the feed didn&apos;t include a shipment ETA for them, so they
          can&apos;t be placed on a month. They are <b>excluded</b> from the
          roll-forward below until an ETA arrives for those units.
        </Alert>
      )}

      <Table size="small" sx={{ backgroundColor: "#ffffff", borderRadius: 1 }}>
        <TableHead>
          <TableRow>
            {[
              "Month",
              "Start of month",
              "Incoming",
              "Forecast",
              "End of month",
              "Status",
            ].map((label) => (
              <TableCell
                key={label}
                sx={{
                  fontWeight: 600,
                  fontSize: "11px",
                  color: "#475569",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  backgroundColor: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                  py: 1,
                  textAlign: label === "Month" ? "left" : "center",
                }}
              >
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {projection.map((m) => (
            <TableRow key={m.monthKey}>
              <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                {m.isRestOfMonth ? (
                  <Tooltip
                    arrow
                    title={`${Math.round(
                      m.restOfMonthRemainingFraction * 100
                    )}% of ${formatMonthLabelWithRestOf(
                      m.monthKey
                    )} remaining — forecast scaled to remaining days`}
                  >
                    <span
                      style={{
                        borderBottom: "1px dashed #94a3b8",
                        cursor: "default",
                      }}
                    >
                      {m.label}
                    </span>
                  </Tooltip>
                ) : (
                  m.label
                )}
              </TableCell>
              <TableCell align="center" sx={{ fontSize: "12px" }}>
                {m.start.toLocaleString()}
              </TableCell>
              <TableCell align="center" sx={{ fontSize: "12px" }}>
                {m.incoming > 0 ? (
                  <Tooltip
                    arrow
                    title={
                      <Box>
                        {m.incomingShipments.map((s, i) => {
                          const meta = [
                            s.container_number,
                            s.forwarder,
                            s.shipping_method,
                          ]
                            .filter(Boolean)
                            .join(" \u00b7 ");
                          const label = s.stage || s.type;
                          return (
                            <div key={i}>
                              {Number(s.quantity).toLocaleString()} units
                              {s.eta ? ` (ETA ${s.eta})` : ""}
                              {label ? ` - ${label}` : ""}
                              {meta ? ` [${meta}]` : ""}
                            </div>
                          );
                        })}
                      </Box>
                    }
                  >
                    <span
                      style={{
                        color: "#0369a1",
                        fontWeight: 600,
                        cursor: "default",
                      }}
                    >
                      +{m.incoming.toLocaleString()}
                    </span>
                  </Tooltip>
                ) : (
                  <span style={{ color: "#94a3b8" }}>0</span>
                )}
              </TableCell>
              <TableCell align="center" sx={{ fontSize: "12px" }}>
                {(() => {
                  const isOverridden =
                    m.forecastFullMonth !== m.forecastOriginal;
                  if (!isOverridden && !m.isRestOfMonth) {
                    return m.forecast.toLocaleString();
                  }

                  const tooltip = (
                    <Box sx={{ maxWidth: 280 }}>
                      {m.isRestOfMonth && (
                        <div>
                          Rest of month: {m.forecast.toLocaleString()} of{" "}
                          {m.forecastFullMonth.toLocaleString()} full-month
                          forecast (
                          {Math.round(m.restOfMonthRemainingFraction * 100)}%
                          remaining)
                        </div>
                      )}
                      {isOverridden && (
                        <div style={{ marginTop: m.isRestOfMonth ? 6 : 0 }}>
                          Original forecast:{" "}
                          {m.forecastOriginal.toLocaleString()}
                        </div>
                      )}
                      {m.overrideReason && (
                        <div style={{ marginTop: 6 }}>
                          <b>Reason:</b> {m.overrideReason}
                        </div>
                      )}
                      {m.overrideNotes && (
                        <div
                          style={{
                            marginTop: 4,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          <b>Notes:</b> {m.overrideNotes}
                        </div>
                      )}
                    </Box>
                  );

                  return (
                    <Tooltip arrow title={tooltip}>
                      <span
                        style={{
                          color: isOverridden ? "#2563eb" : "#0f766e",
                          fontWeight: 600,
                          borderBottom: m.isRestOfMonth
                            ? "1px dashed currentColor"
                            : undefined,
                          cursor: "default",
                        }}
                      >
                        {m.forecast.toLocaleString()}
                      </span>
                    </Tooltip>
                  );
                })()}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color:
                    m.end < 0
                      ? "#991b1b"
                      : m.status === "low"
                      ? "#92400e"
                      : "#1e293b",
                }}
              >
                {m.end < 0
                  ? `${m.end.toLocaleString()}`
                  : m.end.toLocaleString()}
              </TableCell>
              <TableCell align="center">
                <StatusChip
                  status={m.status}
                  excludedFromPO={m.excludedFromPO}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const OperationsRow = ({
  row,
  index,
  predictedUnits,
  pastUnitsSales,
  sellerForecastReviews,
}) => {
  const [expanded, setExpanded] = useState(false);

  const { predictedItem, sellerForecastItem } = getForecastData(
    row.child_asin,
    predictedUnits,
    sellerForecastReviews
  );

  return (
    <>
      <StyledTableRow
        key={`${row.store}-${row.parent_asin}-${row.child_asin}-${row.sku}`}
        isEvenLogicalRow={row.isEvenLogicalRow}
        isLastInGroup={row.isLastInGroup}
        expanded={expanded}
      >
        <TableCell align="center" sx={{ width: "36px", paddingX: 0 }}>
          <IconButton
            size="small"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "Collapse row" : "Expand row"}
            sx={{ color: "#64748b" }}
          >
            {expanded ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell align="center">
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#64748b" }}
          >
            {index + 1}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.store}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", fontSize: "12px", color: "#475569" }}
          >
            {row.parent_asin}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", fontSize: "12px", color: "#475569" }}
          >
            {row.child_asin}
          </Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "12px",
                color: "#475569",
                fontWeight: 500,
              }}
            >
              {row.sku}
            </Typography>
            {row.noActiveInventory && (
              <Tooltip title="Out of Stock" arrow placement="top">
                <Chip
                  label="OoS"
                  size="small"
                  sx={{
                    backgroundColor: "#fee2e2",
                    color: "#991b1b",
                    fontSize: "10px",
                    height: "18px",
                    fontWeight: 600,
                  }}
                />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell align="center">
          {row.cogs != null ? (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              ${Number(row.cogs).toFixed(2)}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              —
            </Typography>
          )}
        </TableCell>
        <TableCell align="center">
          {row.dos != null ? (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {Math.round(Number(row.dos))}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              —
            </Typography>
          )}
        </TableCell>
        <TableCell align="center">
          {(() => {
            const projection = buildMonthlyProjection(
              row,
              predictedItem,
              sellerForecastItem
            );
            // The amount actually kept out of the PO for an excluded month is
            // its roll-forward shortfall (unmet demand), not its full forecast:
            // inventory may already cover part of that month.
            const excludedMonths = projection
              .filter((m) => m.excludedFromPO)
              .map((m) => ({ ...m, shortfall: Math.max(-m.end, 0) }));
            const excludedTotal = excludedMonths.reduce(
              (sum, m) => sum + m.shortfall,
              0
            );
            const po = calculatePOFromProjection(projection);

            const value = (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  cursor: excludedTotal > 0 ? "default" : "inherit",
                  ...(excludedTotal > 0
                    ? {
                        borderBottom: "1px dashed #94a3b8",
                        display: "inline-block",
                      }
                    : {}),
                }}
              >
                {po}
              </Typography>
            );

            if (excludedTotal > 0) {
              return (
                <Tooltip
                  arrow
                  title={
                    <Box>
                      <div>
                        Excluded from PO (OOS within ~{PO_LEAD_TIME_DAYS} days):
                      </div>
                      {excludedMonths.map((m) => (
                        <div key={m.monthKey}>
                          {m.label}: {m.shortfall.toLocaleString()} units
                        </div>
                      ))}
                      <div style={{ marginTop: 4 }}>
                        Total excluded: {excludedTotal.toLocaleString()}
                      </div>
                    </Box>
                  }
                >
                  {value}
                </Tooltip>
              );
            }
            return value;
          })()}
        </TableCell>
        <TableCell align="center">
          <Tooltip
            title={
              <Box>
                <div>FBA: {row.fba_inventory}</div>
                <div>AWD: {row.awd_inventory}</div>
                <div>3PL: {row["3pl_inventory"]}</div>
              </Box>
            }
            arrow
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, cursor: "default" }}
            >
              {row.fba_inventory + row.awd_inventory + row["3pl_inventory"]}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell align="center">
          <Tooltip
            title={
              <Box>
                <div>FBA: {row.fba_inventory}</div>
                <div>AWD: {row.awd_inventory}</div>
                <div>3PL: {row["3pl_inventory"]}</div>
                <div>In Work: {row.in_work_inventory}</div>
                <div>On The Way: {row.on_the_way_inventory}</div>
                <div>AWD Outbound: {row.awd_outbound}</div>
                <div>AWD Inbound: {row.awd_inbound}</div>
              </Box>
            }
            arrow
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, cursor: "default" }}
            >
              {row.all_inventory}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell align="center">
          {(() => {
            const { original, override, hasOverride } = getForecastData(
              row.child_asin,
              predictedUnits,
              sellerForecastReviews
            );

            if (hasOverride) {
              return (
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography
                    variant="body2"
                    sx={{
                      textDecoration: "line-through",
                      color: "#94a3b8",
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    {original}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#2563eb",
                      fontSize: "14px",
                      fontWeight: 700,
                    }}
                  >
                    {override}
                  </Typography>
                </Box>
              );
            }
            return (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                {original}
              </Typography>
            );
          })()}
        </TableCell>
        <TableCell align="center">
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "#64748b" }}
          >
            {pastUnitsSales.find(
              ({ parent_asin, child_asin, sku }) =>
                parent_asin === row.parent_asin &&
                child_asin === row.child_asin &&
                sku === row.sku
            )?.recent_units_3m || 0}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "#78716c" }}
          >
            {pastUnitsSales.find(
              ({ parent_asin, child_asin, sku }) =>
                parent_asin === row.parent_asin &&
                child_asin === row.child_asin &&
                sku === row.sku
            )?.last_year_units_3m || 0}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "#78716c" }}
          >
            {pastUnitsSales.find(
              ({ parent_asin, child_asin, sku }) =>
                parent_asin === row.parent_asin &&
                child_asin === row.child_asin &&
                sku === row.sku
            )?.last_year_units_next_3m || 0}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "#78716c" }}
          >
            {pastUnitsSales.find(
              ({ parent_asin, child_asin, sku }) =>
                parent_asin === row.parent_asin &&
                child_asin === row.child_asin &&
                sku === row.sku
            )?.last_year_units_next_7m || 0}
          </Typography>
        </TableCell>
      </StyledTableRow>
      <TableRow
        sx={{
          backgroundColor: row.isEvenLogicalRow ? "#f8fafc" : "#ffffff",
          "& > .MuiTableCell-root": {
            padding: 0,
            borderBottom: row.isLastInGroup
              ? "1px solid rgba(224, 224, 224, 1)"
              : "none",
          },
        }}
      >
        <TableCell colSpan={16}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <MonthlyProjectionPanel
              row={row}
              predictedItem={predictedItem}
              sellerForecastItem={sellerForecastItem}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default OperationsRow;
